// A replay's own page (/free-webinars/replay/<id>; Adam, 23 Oct: "if you click on the webinar ... it
// might open up in its own page, but then that's the thing that plays"). Every card for a replay opens
// it: the Library's Watch again and See all, Free webinars' Watch again, and search.
//
// The way back to Free webinars, then the player at the top, 16:9 with the replay's own thumbnail behind
// its play button (ReplayPlayer), so on a phone it's the first thing under the header with no scrolling,
// then the title, its date and its description. Nothing else until they press Play (Adam: "There
// shouldn't be anything below it before they've watched anything"): then its chapters, where it has
// any, and its 1 call to action, only for someone who doesn't own what it sells (my_replay_cta's
// hidden; webinars.js). Someone who owns it sees nothing in its place, ever. On a laptop the 2 sit beside
// the player, in a column kept for them so the player never changes size; narrower, under the
// description. A replay that will never have either for this viewer (no chapters, and no call to action
// for them) has no such column: the player takes the whole width (review of 23 Oct). 1 tap anywhere on
// the player plays it, from where they left off; their place is saved as they watch and when they
// leave, never while staff view as a member (ReplayPlayer's canSave).
//
// The id of 1 still to come goes to its card on Free webinars, where Register on Zoom is; an id that
// isn't a replay that's up says so, under the way back. Someone held back from Free webinars gets
// "nothing here", as on Free webinars.
import { useEffect, useState } from 'react'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { navigate } from '../lib/router.jsx'
import { NotFound } from '../shell/Shell.jsx'
import { useViewingAs } from '../lib/viewAs.jsx'
import { FreePage, LoadFailed } from '../free/FreePage.jsx'
import { webinarsProduct } from '../free/resources.js'
import { webinarPage } from '../free/webinars.js'
import { useWebinarPage } from '../free/useWebinarPage.js'
import { ReplayPlayer } from '../free/ReplayPlayer.jsx'
import { Dot } from '../free/WebinarDot.jsx'
import { useThumbnails } from '../free/thumbnails.js'

const BACK = { to: '/free-webinars', label: 'Free webinars' }
const GONE = "This replay isn't up at the moment. Every replay that's up is on Free webinars."

export default function WebinarReplay({ id }) {
  const { me, lib, refreshLibrary } = useMember()
  const nowMs = useNow()
  const { page, load, savePosition } = useWebinarPage(me.person.id)

  const ready = Boolean(lib.data) && page.status === 'ready'
  const view = ready ? webinarPage({ ...page.data, lib: lib.data, nowMs }) : null
  const r = view?.replays.find(x => x.id === id) || null
  const upcoming = Boolean(view?.upcoming.some(w => w.id === id))
  useEffect(() => {
    if (upcoming) navigate(`/free-webinars#webinar-${encodeURIComponent(id)}`, { replace: true })
  }, [upcoming, id])

  // held back from Free webinars: their row isn't there to read
  if (lib.data && !webinarsProduct(lib.data)) return <NotFound />

  const failed = lib.status === 'error' || page.status === 'error'
  const retry = () => {
    if (lib.status === 'error') refreshLibrary()
    if (page.status === 'error') load()
  }

  return (
    <FreePage title={r ? `Free webinars, ${r.title}` : 'Free webinars'} variant="replay" back={BACK}>
      {failed && <LoadFailed onRetry={retry} />}
      {/* the player's 16:9 box while it loads, so the page doesn't move when it comes */}
      {!failed && !ready && <div className="fw-watch" aria-busy="true"><div className="fw-watch-main"><div className="fw-player" /></div></div>}
      {r && <Replay r={r} onPosition={savePosition} />}
      {ready && !r && !upcoming && <p className="fp-empty">{GONE}</p>}
    </FreePage>
  )
}

function Replay({ r, onPosition }) {
  const viewingAs = useViewingAs()
  const thumbs = useThumbnails([r])
  const thumb = thumbs.frame(r)
  const [seek, setSeek] = useState(null)
  // pressed Play, and it's playing: only then the chapters and the call to action
  const [started, setStarted] = useState(false)
  // known before Play, so the player is the same size before and after it
  const hasSide = r.chapters.length > 0 || Boolean(r.cta)
  const side = started && hasSide
  return (
    <article className={hasSide ? 'fw-watch' : 'fw-watch fw-watch--solo'} aria-labelledby="fw-watch-title">
      <div className="fw-watch-main">
        <ReplayPlayer title={r.title} vimeo={r.vimeo} savedSeconds={r.savedSeconds} seek={seek}
          onPosition={seconds => onPosition(r.id, seconds)} onStart={() => setStarted(true)} canSave={!viewingAs}
          poster={thumb?.url || null} onPosterError={e => thumbs.failed(r, e.currentTarget.src)}
          onPosterLoad={e => thumbs.loaded(r, e.currentTarget.src)} />
        <div className="fw-watch-text">
          <span className="fw-status"><Dot look={r.sellsLook} name={r.sellsName} />Replay</span>
          <h1 id="fw-watch-title" className="fw-watch-title">{r.title}</h1>
          <span className="fw-time">{r.date}</span>
          {r.description && <p className="fw-desc fw-watch-desc">{r.description}</p>}
        </div>
      </div>
      {side && (
        <div className="fw-side">
          {r.chapters.length > 0 && (
            <div className="fw-chapters">
              <h2 className="fw-chapters-head">Chapters</h2>
              {r.chapters.map((c, i) => (
                <button key={i} type="button" className="fw-chap" onClick={() => setSeek({ seconds: c.seconds, n: Date.now() })}>
                  <span className="fw-chap-time">{c.time}</span><span className="fw-chap-title">{c.title}</span>
                </button>
              ))}
            </div>
          )}
          {r.cta && (
            <div className="fw-box">
              {r.cta.sellsName && <span className="fw-box-eyebrow"><Dot look={r.cta.sellsLook} name={r.cta.sellsName} />{r.cta.sellsName}</span>}
              {r.cta.text && <span className="fw-box-line">{r.cta.text}</span>}
              <a className="fw-box-btn" href={r.cta.url} target="_blank" rel="noopener noreferrer">{r.cta.label}</a>
            </div>
          )}
        </div>
      )}
    </article>
  )
}
